"use client"

import Image from "next/image"
import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { CheckCircle, Users, Award, Clock } from "lucide-react"
import TextReveal from "@/components/animated/text-reveal"
import FloatingCard from "@/components/animated/floating-card"
import GradientBackground from "@/components/animated/gradient-background"

export default function AboutPageClient() {
  return (
    <div className="min-h-screen relative">
      <GradientBackground />

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-cyan-600/20" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm">
                About Real Metro Metal
              </Badge>
            </motion.div>
            <TextReveal>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">About Real Metro Metal - Leading Industrial Hardware Supplier in Sri Lanka</h1>
            </TextReveal>
            <TextReveal delay={0.2}>
              <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
                With years of experience in the industry, Real Metro Metal has established itself as a leading wholesale
                hardware supplier in Sri Lanka.
              </p>
            </TextReveal>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Real Metro Metal was founded with a simple mission: to provide high-quality industrial hardware
                  products to businesses across Sri Lanka. Located in the heart of Colombo, we have been serving the
                  construction, manufacturing, and industrial sectors with dedication and expertise.
                </p>
                <p>
                  Our commitment to quality and customer satisfaction has made us a trusted partner for businesses of
                  all sizes. From small contractors to large industrial projects, we provide the hardware solutions that
                  keep Sri Lanka building and growing.
                </p>
                <p>
                  We specialize in a comprehensive range of products including valves, pipes, plates, sheets, H-iron,
                  boxbars, nuts & bolts, and GI conduits & fittings. Our extensive inventory ensures that our customers
                  can find everything they need in one place.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] relative rounded-2xl overflow-hidden shadow-2xl">
                <Image
                  src="/image/aboutfront.png"
                  alt="Real Metro Metal warehouse"
                  fill
                  className="object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50/50 relative">
        <div className="container mx-auto px-4">
          <TextReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Values</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">The principles that guide everything we do</p>
            </div>
          </TextReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: CheckCircle,
                title: "Quality First",
                desc: "We never compromise on quality. Every product meets our strict standards.",
              },
              { icon: Users, title: "Customer Focus", desc: "Our customers are at the heart of everything we do." },
              { icon: Award, title: "Excellence", desc: "We strive for excellence in every aspect of our business." },
              { icon: Clock, title: "Reliability", desc: "You can count on us for consistent, reliable service." },
            ].map((value, index) => (
              <FloatingCard key={value.title} delay={index * 0.1}>
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500 h-full">
                  <CardContent className="p-8 text-center">
                    <motion.div
                      className="bg-gradient-to-br from-slate-900 to-slate-700 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <value.icon className="h-8 w-8" />
                    </motion.div>
                    <h3 className="font-semibold text-lg mb-3">{value.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{value.desc}</p>
                  </CardContent>
                </Card>
              </FloatingCard>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
